import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref
} from 'vue';
import {
  type FilterResult,
  getPlatformServicesResult,
  getPlatformServicesType,
  type PlatformServicesType
} from '@/api/cloud-resource';

let officialStyleInstance: ReturnType<typeof createOfficialStyleInstance> | null = null;

const PAGE_SIZE = 10;

function createOfficialStyleInstance() {
  const pageStart = ref(1);
  const page = ref(1);
  const type1 = ref('');
  const type2 = ref('');
  const type3 = ref('');
  const loading = ref(false);

  const noMore = ref(false);
  const top = computed(() => pageStart.value < 1);

  const searchTerms = ref<string>('');

  const search = computed(() => {
    return searchTerms.value.trim().split(' ').filter(Boolean);
  });

  const typeDatas = ref<PlatformServicesType[]>([]);
  const type1Options = computed(() => {
    const typeSet = new Set(typeDatas.value.map(item => item.type1));
    return [{ text: '大类', value: '' }, ...Array.from(typeSet).map(t => ({ text: t, value: t }))];
  });
  const type2Options = computed(() => {
    const type2List = typeDatas.value.filter(item => item.type1 === type1.value)
      .map(item => item.type2);
    const typeSet = new Set(type2List);
    return [{ text: '中类', value: '' }, ...Array.from(typeSet).map(t => ({ text: t, value: t }))];
  });
  const type3Options = computed(() => {
    const type3List = typeDatas.value.filter(item => item.type2 === type2.value)
      .map(item => item.type3);
    const typeSet = new Set(type3List);
    return [{ text: '小类', value: '' }, ...Array.from(typeSet).map(t => ({ text: t, value: t }))];
  });

  const results = ref<(FilterResult & { anchor: string })[]>([]);

  onMounted(async () => {
    typeDatas.value = await getPlatformServicesType();
    loadFilter();

    const hash = localStorage.getItem('lastOfficialStyleHash') || '';
    const anchor = parseInt(hash);
    if (anchor) {
      const p = Math.ceil(anchor / PAGE_SIZE);
      page.value = p;
      pageStart.value = p - 1;

      results.value = [];
      await loadNextPage();

      await nextTick(() => {
        const ele = document.getElementById(`style-${anchor}`);
        if (ele) {
          ele.style.border = '2px solid #ccc';
          ele.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });

      localStorage.setItem('lastOfficialStyleHash', '');
    } else {
      await reload();
    }
  });

  onUnmounted(() => {
    officialStyleInstance = null;
  });

  const onFilterChange = async (type: 'type1' | 'type2' | 'type3', value: string) => {
    switch (type) {
      case 'type1':
        type1.value = value;
        type2.value = '';
        type3.value = '';
        saveFilter();
        break;
      case 'type2':
        type2.value = value;
        type3.value = '';
        saveFilter();
        break;
      case 'type3':
        type3.value = value;
        saveFilter();
        break;
    }

    await reload();
  };

  const loadFilter = () => {
    const lastFilterStr = localStorage.getItem('lastOfficialStyleFilter');

    if (!lastFilterStr) {
      if (type1Options.value.find(item => item.value === '服装')) {
        type1.value = '服装';
      }
      if (type2Options.value.find(item => item.value === '女装')) {
        type2.value = '女装';
      }
      if (type3Options.value.find(item => item.value === '毛衣')) {
        type3.value = '毛衣';
      }
    } else {
      const lastCloudPageFilter = JSON.parse(lastFilterStr);
      type1.value = lastCloudPageFilter.type1 || '';
      type2.value = lastCloudPageFilter.type2 || '';
      type3.value = lastCloudPageFilter.type3 || '';
    }

    const lastCloudPageSearch = localStorage.getItem('lastCloudPageSearch');
    searchTerms.value = lastCloudPageSearch || '';
  };

  const saveFilter = () => {
    localStorage.setItem('lastOfficialStyleFilter', JSON.stringify({
      type1: type1.value,
      type2: type2.value,
      type3: type3.value
    }));
  };

  async function loadData(page = 1, keyword: string[] = [], pageSize: number = PAGE_SIZE) {
    const resp = await getPlatformServicesResult({
      page,
      pageSize,
      type1: type1.value,
      type2: type2.value,
      type3: type3.value,
      search: [...keyword]
    });

    return resp.data.map((item, index) => ({
      ...item,
      anchor: `${(page - 1) * PAGE_SIZE + index + 1}`
    }));
  }

  const reload = async () => {
    pageStart.value = 0;
    page.value = 1;
    results.value = [];
    await loadNextPage();
  };

  const loadNextPage = async () => {
    loading.value = true;
    const result = await loadData(page.value, search.value, PAGE_SIZE);
    loading.value = false;
    page.value += 1;
    results.value = [...results.value, ...result];
    noMore.value = result.length < PAGE_SIZE;
  };

  const loadPrevPage = async () => {
    if (pageStart.value < 1) {
      return;
    }
    loading.value = true;
    const result = await loadData(pageStart.value, search.value, PAGE_SIZE);
    loading.value = false;
    pageStart.value -= 1;
    results.value = [...result, ...results.value];
  };

  return {
    results,
    type1,
    type2,
    type3,
    searchTerms,
    onFilterChange,
    type1Options,
    type2Options,
    type3Options,
    loadData,
    reload,
    loadNextPage,
    loadPrevPage,
    noMore,
    top,
    loading
  };
}

export const useOfficialStyle = () => {
  if (!officialStyleInstance) {
    officialStyleInstance = createOfficialStyleInstance();
  }
  return officialStyleInstance;
};
